import React from 'react';
import { Box, Button, Typography, Paper, styled } from '@mui/material';
import { WaterQualityData } from '../../types/waterQuality';
import WaterParameterGauges from './WaterParameterGauges';
import MineralContentChart from './MineralContentChart';
import WaterQualityScore from './WaterQualityScore';
import CropCompatibilityChart from './CropCompatibilityChart';
import { DownloadOutlined, ShareOutlined, PrintOutlined } from '@mui/icons-material';

interface WaterQualityVisualizationProps {
  waterData: WaterQualityData;
  showExport?: boolean;
  title?: string;
}

const VisualizationContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const VisualizationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const VisualizationTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const VisualizationActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'space-between',
  },
}));

export const WaterQualityVisualization: React.FC<WaterQualityVisualizationProps> = ({
  waterData,
  showExport = true,
  title = 'Water Quality Analysis',
}) => {
  // Handle exports
  const handleExportPDF = () => {
    // Implementation for PDF export would go here
    console.log('Export to PDF');
  };

  const handleShare = () => {
    // Implementation for sharing would go here
    console.log('Share visualization');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <VisualizationContainer className="water-quality-visualization">
      <VisualizationHeader>
        <VisualizationTitle variant="h5">{title}</VisualizationTitle>
        
        {showExport && (
          <VisualizationActions>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={handleExportPDF}
              size="small"
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareOutlined />}
              onClick={handleShare}
              size="small"
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintOutlined />}
              onClick={handlePrint}
              size="small"
            >
              Print
            </Button>
          </VisualizationActions>
        )}
      </VisualizationHeader>

      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Water Profile: {waterData.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This analysis provides insights into your water quality parameters and their impact on hydroponic cultivation.
        </Typography>
      </Paper>

      {/* Water quality score section */}
      <WaterQualityScore waterData={waterData} />
      
      {/* Key parameters section */}
      <WaterParameterGauges waterData={waterData} />
      
      {/* Mineral content section */}
      <MineralContentChart waterData={waterData} />
      
      {/* Crop compatibility section */}
      <CropCompatibilityChart waterData={waterData} />
    </VisualizationContainer>
  );
};

export default WaterQualityVisualization;
